import React, { useEffect, useState } from 'react';
import { arrayOf, func, instanceOf, number, shape, string } from 'prop-types';
import { reportToSegment, types } from '@smartcar/morse';
import { Box, Divider, Typography } from '@mui/material';

import staticText from '../../../../../../../../localization/Application/webhooks';
import { ReviewContainer, WebhookDetails, WebhookPayload } from './styles';
import SignalsList from './components/SignalsList/SignalsList';
import { TabbedCodeBlock } from '../../../../../../../../components';
import { formatWebhookForm } from '../../util';
import api from '../../../../../../../../services/api/api';
import { interpolateOptionsWithVehicleCount } from '../util';

const WebhookReview = ({
  signals,
  formValues,
  setNextEnabled,
  selectedSignalCount,
  webhook,
  applicationId,
  liveVehicleCount,
}) => {
  const { fields } = staticText.addModal.stepperSections[2];
  const content = staticText.addModal.stepperSections[3];

  // Not using the redux store's `testEvent` because this
  // sample event needs to reflect the current form values
  // when a webhook is being edited
  const [sampleEvent, setSampleEvent] = useState(null);
  const [errorFetchingSampleEvent, setErrorFetchingSampleEvent] = useState(false);

  const fetchSampleEvent = async () => {
    try {
      const { data } = await api.fetchSamplePayloadForSampleWebhook(
        applicationId,
        {
          ...formatWebhookForm(formValues),
          webhookId: (webhook && webhook.id) || null,
          applicationId,
        },
      );
      setSampleEvent(data);
    } catch (error) {
      setErrorFetchingSampleEvent(true);
    }
  };

  useEffect(() => {
    setNextEnabled(true);
    fetchSampleEvent();
    reportToSegment(types.PAGE, 'Webhook Form - Review');
  }, []);

  return (
    <Box flexGrow={1} display="flex" flexDirection="column">
      <Typography variant="h1">
        {content.title}
      </Typography>
      <ReviewContainer>
        <WebhookDetails>
          {content.fieldOrder.map((fieldKey) => {
            const field = fields.find(f => f.key === fieldKey);
            const fieldName = field.name;
            const fieldValue = (() => {
              if (field.type !== 'select') {
                return formValues[fieldKey];
              } else if (field.key === 'autoSubscribe') {
                return interpolateOptionsWithVehicleCount(
                  field.options,
                  liveVehicleCount,
                ).find(option => option.value === formValues[fieldKey]).label;
              }
              return field.options.find(option => option.value === formValues[fieldKey]).label;
            })();
            return (
              <div key={fieldKey}>
                <Typography
                  component="h2"
                  variant="body2"
                  sx={theme => ({
                    fontFamily: theme.typography.bold.fontFamily,
                    marginBottom: theme.spacing(1),
                  })}
                >{fieldName}
                </Typography>
                {fieldValue ? (
                  <Typography variant="body2">{fieldValue}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{staticText.webhookDetails.errorCallbackUriFallback}</Typography>
                )}
                <Divider sx={theme => ({ margin: theme.spacing(1, 0, 3) })} />
              </div>
            );
          })}
          <SignalsList
            title={content.vehicleSignals}
            signals={signals}
            selectedSignals={formValues.signalNames}
            selectedSignalCount={selectedSignalCount}
          />
        </WebhookDetails>
        <WebhookPayload>
          <TabbedCodeBlock
            styles={{
              maxHeight: '100%',
            }}
            tabs={[
              {
                label: staticText.webhookDetails.payload.title,
                code: JSON.stringify(sampleEvent && sampleEvent.sampleData, null, 2),
                error: errorFetchingSampleEvent && staticText.webhookDetails.payload.fetchError,
              },
              {
                label: staticText.webhookDetails.payload.errorTitle,
                code: (sampleEvent && sampleEvent.sampleError)
                  ? JSON.stringify(sampleEvent.sampleError, null, 2) : null,
              },
            ]}
          />
        </WebhookPayload>
      </ReviewContainer>
    </Box>
  );
};

export default WebhookReview;

WebhookReview.propTypes = {
  signals: shape({
    [string]: arrayOf(shape({
      name: string.isRequired,
      groupName: string.isRequired,
      description: string.isRequired,
    })).isRequired,
  }).isRequired,
  formValues: shape({
    signalNames: instanceOf(Set).isRequired,
    name: string.isRequired,
    callbackUri: string.isRequired,
    errorCallbackUri: string.isRequired,
    units: string.isRequired,
    rules: arrayOf(string).isRequired,
  }).isRequired,
  setNextEnabled: func.isRequired,
  selectedSignalCount: number.isRequired,
  webhook: shape({
    id: string.isRequired,
  }),
  applicationId: string.isRequired,
  liveVehicleCount: number,
};

WebhookReview.defaultProps = {
  webhook: undefined,
  liveVehicleCount: undefined,
};
